/**
 * GRANULLO PLATFORM EVOLUTION — Commit Economy Engine
 * 
 * SINGLE SOURCE OF TRUTH for all commit calculations across the platform.
 * 
 * This is the ONLY commit engine. All surfaces must use this:
 * - Program dashboards
 * - Simulations
 * - Homepage scenarios
 * - Demo environment
 * - Founder simulator
 * - Investor sandbox
 * - Production systems
 * 
 * NO DUPLICATE PRICING LOGIC MAY EXIST.
 * 
 * Commit Model:
 * 1 commit = $10 (base reference price, versioned)
 * 
 * Commits derive from three sources:
 * - Topology: system complexity
 * - Throughput: transaction volume
 * - Monitoring: signal complexity
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// ============================================================================
// COMMIT ECONOMY CONSTANTS
// ============================================================================

/**
 * Economic Version - Existing programs lock to their version
 */
export const ECONOMIC_VERSIONS = {
  'v1.0': { commitPrice: 10, effectiveDate: '2026-01-01' },
  'v1.1': { commitPrice: 12, effectiveDate: '2026-06-01' },
  'v2.0': { commitPrice: 15, effectiveDate: '2027-01-01' }
};

export const CURRENT_ECONOMIC_VERSION = 'v1.0';

/**
 * Topology Weights - How program structure generates commits
 */
export const TOPOLOGY_WEIGHTS = {
  BENEFICIARIES: 0.30,       // Per beneficiary
  MERCHANTS: 0.50,           // Per merchant
  AUTHORITY_NODES: 2.0,      // Per authority node
  PROGRAM_LAYERS: 3.0        // Per programmability layer
};

/**
 * Throughput Configuration
 */
export const THROUGHPUT_CONFIG = {
  TRANSACTIONS_PER_COMMIT: 10000  // transactions ÷ 10,000 = commits
};

/**
 * Monitoring Signal Weights
 */
export const MONITORING_WEIGHTS = {
  STRUCTURAL: 5.0,           // Structural signal
  BEHAVIOUR: 2.0,            // Behaviour signal
  INFORMATIONAL: 0.5         // Informational signal
};

/**
 * Monitoring Cap
 */
export const MONITORING_CAP_PERCENT = 15;  // ≤ 15% of commit budget

/**
 * Envelope Caps by Program Type
 */
export const ENVELOPE_CAPS = {
  COMMERCIAL: 0.10,          // ≤ 10% of program value
  GOVERNMENT: 0.10,          // ≤ 10% of program value
  CSR: 0.06,                 // ≤ 6% of program value
  DONATION: 0.06,            // ≤ 6% of program value
  DEFAULT: 0.10              // Default cap
};

/**
 * Bank Infrastructure Share Cap
 */
export const BANK_SHARE_CAP_PERCENT = 10;  // ≤ 10% of total commit budget

/**
 * Program Initialization Commits
 */
export const INITIALIZATION_COMMITS = 50;  // Per program

/**
 * Baseline Buffer Rules - Prevent artificially small baselines
 */
export const BASELINE_BUFFERS = {
  BENEFICIARIES: 0.25,       // +25%
  MERCHANTS: 0.25,           // +25%
  TRANSACTION_THROUGHPUT: 0.15,  // +15%
  AUTHORITY_NODES: 0.0       // +0%
};

/**
 * Program Type Classification
 */
export const PROGRAM_TYPES = {
  CBDC: 'commercial',
  EMPLOYEE_BENEFITS: 'commercial',
  VC_PE_FUNDING: 'commercial',
  STARTUP_GRANTS: 'commercial',
  CSR: 'csr',
  DONATION: 'donation',
  CLIMATE: 'government',
  CIRCULARITY: 'commercial',
  GOVERNMENT_WELFARE: 'government'
};

// ============================================================================
// COMMIT CALCULATION ENGINE
// ============================================================================

/**
 * Calculate Topology Commits
 * Formula: (beneficiaries × 0.30) + (merchants × 0.50) + (authority_nodes × 2.0) + (program_layers × 3.0)
 */
export function calculateTopologyCommits(topology) {
  const {
    beneficiariesCount = 0,
    merchantsCount = 0,
    authorityNodes = 0,
    programLayers = 1
  } = topology;
  
  return (
    (beneficiariesCount * TOPOLOGY_WEIGHTS.BENEFICIARIES) +
    (merchantsCount * TOPOLOGY_WEIGHTS.MERCHANTS) +
    (authorityNodes * TOPOLOGY_WEIGHTS.AUTHORITY_NODES) +
    (programLayers * TOPOLOGY_WEIGHTS.PROGRAM_LAYERS)
  );
}

/**
 * Calculate Throughput Commits
 * Formula: transactions ÷ 10,000
 */
export function calculateThroughputCommits(transactionCount) {
  return transactionCount / THROUGHPUT_CONFIG.TRANSACTIONS_PER_COMMIT;
}

/**
 * Calculate Monitoring Commits
 * Weights: structural=5, behaviour=2, informational=0.5
 */
export function calculateMonitoringCommits(signals) {
  const {
    structuralSignals = 0,
    behaviourSignals = 0,
    informationalSignals = 0
  } = signals;
  
  return (
    (structuralSignals * MONITORING_WEIGHTS.STRUCTURAL) +
    (behaviourSignals * MONITORING_WEIGHTS.BEHAVIOUR) +
    (informationalSignals * MONITORING_WEIGHTS.INFORMATIONAL)
  );
}

/**
 * Calculate Envelope Limit
 * commit_budget ≤ program_value × envelope_rate
 */
export function calculateEnvelopeLimit(programValue, programType) {
  const type = PROGRAM_TYPES[programType] || 'commercial';
  const cap = ENVELOPE_CAPS[type.toUpperCase()] || ENVELOPE_CAPS.DEFAULT;
  const commitPrice = ECONOMIC_VERSIONS[CURRENT_ECONOMIC_VERSION].commitPrice;
  
  return (programValue * cap) / commitPrice;
}

/**
 * Calculate Bank Infrastructure Share
 * bank_share ≤ 10% of total commit budget
 */
export function calculateBankShare(totalCommits, requestedBankCommits) {
  const maxBankShare = totalCommits * (BANK_SHARE_CAP_PERCENT / 100);
  return Math.min(requestedBankCommits, maxBankShare);
}

/**
 * Apply Monitoring Cap
 * Monitoring commits ≤ 15% of program commit budget
 */
export function applyMonitoringCap(monitoringCommits, totalCommits) {
  const maxMonitoring = totalCommits * (MONITORING_CAP_PERCENT / 100);
  return Math.min(monitoringCommits, maxMonitoring);
}

/**
 * Apply Baseline Buffers
 * Prevent artificially small baselines
 */
export function applyBaselineBuffers(baseline) {
  return {
    beneficiariesCount: Math.ceil(baseline.beneficiariesCount * (1 + BASELINE_BUFFERS.BENEFICIARIES)),
    merchantsCount: Math.ceil(baseline.merchantsCount * (1 + BASELINE_BUFFERS.MERCHANTS)),
    transactionExpectation: Math.ceil(baseline.transactionExpectation * (1 + BASELINE_BUFFERS.TRANSACTION_THROUGHPUT)),
    authorityNodes: baseline.authorityNodes  // No buffer
  };
}

/**
 * MAIN COMMIT CALCULATION - Single Deterministic Engine
 * This is the ONLY function that should calculate commits.
 */
export function calculateProgramCommits(programData) {
  const {
    programValue = 0,
    programType = 'COMMERCIAL',
    beneficiariesCount = 0,
    merchantsCount = 0,
    authorityNodes = 0,
    programLayers = 1,
    transactionExpectation = 0,
    signals = {},
    bankInfrastructureRequest = 0,
    economicVersion = CURRENT_ECONOMIC_VERSION
  } = programData;
  
  // 1. Calculate Topology Commits
  const topologyCommits = calculateTopologyCommits({
    beneficiariesCount,
    merchantsCount,
    authorityNodes,
    programLayers
  });
  
  // 2. Calculate Throughput Commits
  const throughputCommits = calculateThroughputCommits(transactionExpectation);
  
  // 3. Calculate Monitoring Commits (before cap)
  const rawMonitoringCommits = calculateMonitoringCommits(signals);
  
  // 4. Calculate base total (before monitoring cap)
  const baseCommits = topologyCommits + throughputCommits + INITIALIZATION_COMMITS;
  
  // 5. Apply Monitoring Cap
  const monitoringCommits = applyMonitoringCap(rawMonitoringCommits, baseCommits);
  
  // 6. Calculate total commits (before envelope)
  const calculatedCommits = baseCommits + monitoringCommits;
  
  // 7. Calculate Envelope Limit
  const envelopeLimit = calculateEnvelopeLimit(programValue, programType);
  
  // 8. Apply Envelope (MIN of calculated and envelope)
  const envelopedCommits = Math.min(calculatedCommits, envelopeLimit);
  
  // 9. Calculate Bank Infrastructure Share
  const bankCommits = calculateBankShare(envelopedCommits, bankInfrastructureRequest);
  
  // 10. Calculate Governance Commits (reserved)
  const governanceCommits = Math.ceil(envelopedCommits * 0.05);  // 5% governance reserve
  
  // 11. Calculate Reserve Commits
  const reserveCommits = Math.ceil(envelopedCommits * 0.03);  // 3% reserve
  
  // 12. Final commit total
  const totalCommits = envelopedCommits;
  
  // 13. Get economic version pricing
  const version = ECONOMIC_VERSIONS[economicVersion] || ECONOMIC_VERSIONS[CURRENT_ECONOMIC_VERSION];
  const commitPrice = version.commitPrice;
  const totalCost = totalCommits * commitPrice;
  
  return {
    // Commit Grid Breakdown
    grid: {
      topology: Math.round(topologyCommits * 100) / 100,
      throughput: Math.round(throughputCommits * 100) / 100,
      monitoring: Math.round(monitoringCommits * 100) / 100,
      governance: governanceCommits,
      bankInfrastructure: Math.round(bankCommits * 100) / 100,
      reserve: reserveCommits,
      initialization: INITIALIZATION_COMMITS
    },
    
    // Totals
    calculatedCommits: Math.round(calculatedCommits * 100) / 100,
    envelopeLimit: Math.round(envelopeLimit * 100) / 100,
    totalCommits: Math.round(totalCommits * 100) / 100,
    
    // Economics
    economicVersion,
    commitPrice,
    totalCost: Math.round(totalCost * 100) / 100,
    
    // Compliance
    envelopeCompliant: calculatedCommits <= envelopeLimit,
    monitoringCapCompliant: rawMonitoringCommits <= (baseCommits * MONITORING_CAP_PERCENT / 100),
    bankShareCompliant: bankCommits <= (totalCommits * BANK_SHARE_CAP_PERCENT / 100),
    
    // Raw values (for debugging)
    _raw: {
      calculatedCommits,
      rawMonitoringCommits,
      envelopeLimit,
      programValue,
      programType
    }
  };
}

// ============================================================================
// COMMIT ECONOMY CONTEXT
// ============================================================================

const CommitEconomyContext = createContext(null);

export function CommitEconomyProvider({ children }) {
  // Global commit counters
  const [globalCounters, setGlobalCounters] = useState({
    commitsGenerated: 0,
    commitsConsumed: 0,
    commitsRemaining: 0,
    monitoringCommits: 0,
    bankCommits: 0,
    grantCommits: 0
  });
  
  // SVUG Tracking (System Value Under Governance)
  const [svugMetrics, setSvugMetrics] = useState({
    totalValue: 0,
    activePrograms: 0,
    averageIntegrityScore: 0,
    totalCommitsConsumed: 0
  });
  
  // Economic control panel state (for operators)
  const [economicControls, setEconomicControls] = useState({
    currentVersion: CURRENT_ECONOMIC_VERSION,
    topologyWeights: TOPOLOGY_WEIGHTS,
    monitoringWeights: MONITORING_WEIGHTS,
    envelopeCaps: ENVELOPE_CAPS,
    bankShareCap: BANK_SHARE_CAP_PERCENT,
    monitoringCap: MONITORING_CAP_PERCENT
  });
  
  // Grant Pool
  const [grantPool, setGrantPool] = useState({
    totalGrants: 10000,       // Total grant commits available
    allocatedGrants: 0,
    remainingGrants: 10000
  });
  
  // ============================================================================
  // PHASE 8: ORGANIZATION RESERVE POOL
  // ============================================================================
  // Reserve Pool is an allocation/accounting surface, not a new economic engine.
  // It derives from canonical commit accounting only.
  // Commits cannot be encashed, converted to fiat, or transferred across institutions.
  // Reserve is institution-bound governance capacity.
  
  const [reservePool, setReservePool] = useState({
    totalReserve: 0,          // Total commits in reserve
    allocatedFromReserve: 0,  // Commits allocated to programs from reserve
    availableReserve: 0,      // Available for allocation
    donatedToGrants: 0,       // Donated to grant commit pool
    purchasedDirectly: 0,     // Purchased directly into reserve
    economicVersion: CURRENT_ECONOMIC_VERSION,
    history: []               // Activity history
  });
  
  /**
   * RESERVE POOL: Move unused commits from program to reserve
   * Program → Reserve Pool
   */
  const moveToReserve = useCallback((programId, amount, metadata = {}) => {
    if (amount <= 0) {
      return { success: false, reason: 'Amount must be positive' };
    }
    
    const timestamp = new Date().toISOString();
    
    setReservePool(prev => ({
      ...prev,
      totalReserve: prev.totalReserve + amount,
      availableReserve: prev.availableReserve + amount,
      history: [
        ...prev.history,
        {
          type: 'MOVE_TO_RESERVE',
          programId,
          amount,
          timestamp,
          metadata
        }
      ]
    }));
    
    return { 
      success: true, 
      moved: amount, 
      timestamp,
      newAvailableReserve: reservePool.availableReserve + amount
    };
  }, [reservePool.availableReserve]);
  
  /**
   * RESERVE POOL: Allocate commits from reserve to a new program
   * Reserve Pool → New Program
   */
  const allocateFromReserve = useCallback((programId, amount, metadata = {}) => {
    if (amount <= 0) {
      return { success: false, reason: 'Amount must be positive' };
    }
    
    if (amount > reservePool.availableReserve) {
      return { 
        success: false, 
        reason: 'Insufficient reserve capacity',
        requested: amount,
        available: reservePool.availableReserve
      };
    }
    
    const timestamp = new Date().toISOString();
    
    setReservePool(prev => ({
      ...prev,
      allocatedFromReserve: prev.allocatedFromReserve + amount,
      availableReserve: prev.availableReserve - amount,
      history: [
        ...prev.history,
        {
          type: 'ALLOCATE_TO_PROGRAM',
          programId,
          amount,
          timestamp,
          metadata
        }
      ]
    }));
    
    return { 
      success: true, 
      allocated: amount, 
      programId,
      timestamp,
      remainingReserve: reservePool.availableReserve - amount
    };
  }, [reservePool.availableReserve]);
  
  /**
   * RESERVE POOL: Purchase commits directly into reserve
   * New Purchase → Reserve Pool
   */
  const purchaseToReserve = useCallback((amount, economicVersion = CURRENT_ECONOMIC_VERSION, metadata = {}) => {
    if (amount <= 0) {
      return { success: false, reason: 'Amount must be positive' };
    }
    
    const commitPrice = ECONOMIC_VERSIONS[economicVersion]?.commitPrice || ECONOMIC_VERSIONS[CURRENT_ECONOMIC_VERSION].commitPrice;
    const totalCost = amount * commitPrice;
    const timestamp = new Date().toISOString();
    
    setReservePool(prev => ({
      ...prev,
      totalReserve: prev.totalReserve + amount,
      availableReserve: prev.availableReserve + amount,
      purchasedDirectly: prev.purchasedDirectly + amount,
      history: [
        ...prev.history,
        {
          type: 'PURCHASE_TO_RESERVE',
          amount,
          totalCost,
          commitPrice,
          economicVersion,
          timestamp,
          metadata
        }
      ]
    }));
    
    return { 
      success: true, 
      purchased: amount, 
      totalCost,
      commitPrice,
      economicVersion,
      timestamp
    };
  }, []);
  
  /**
   * RESERVE POOL: Donate commits to Grant Commit Pool
   * Reserve Pool → Grant Commit Pool
   */
  const donateToGrantPool = useCallback((amount, metadata = {}) => {
    if (amount <= 0) {
      return { success: false, reason: 'Amount must be positive' };
    }
    
    if (amount > reservePool.availableReserve) {
      return { 
        success: false, 
        reason: 'Insufficient reserve capacity for donation',
        requested: amount,
        available: reservePool.availableReserve
      };
    }
    
    const timestamp = new Date().toISOString();
    
    // Update reserve pool
    setReservePool(prev => ({
      ...prev,
      availableReserve: prev.availableReserve - amount,
      donatedToGrants: prev.donatedToGrants + amount,
      history: [
        ...prev.history,
        {
          type: 'DONATE_TO_GRANTS',
          amount,
          timestamp,
          metadata
        }
      ]
    }));
    
    // Update grant pool
    setGrantPool(prev => ({
      ...prev,
      totalGrants: prev.totalGrants + amount,
      remainingGrants: prev.remainingGrants + amount
    }));
    
    return { 
      success: true, 
      donated: amount, 
      timestamp,
      newGrantPoolTotal: grantPool.totalGrants + amount
    };
  }, [reservePool.availableReserve, grantPool.totalGrants]);
  
  /**
   * RESERVE POOL: Get reserve summary for display
   */
  const getReserveSummary = useCallback(() => {
    return {
      totalReserve: reservePool.totalReserve,
      availableReserve: reservePool.availableReserve,
      allocatedFromReserve: reservePool.allocatedFromReserve,
      donatedToGrants: reservePool.donatedToGrants,
      purchasedDirectly: reservePool.purchasedDirectly,
      economicVersion: reservePool.economicVersion,
      recentHistory: reservePool.history.slice(-10)
    };
  }, [reservePool]);
  
  /**
   * RESERVE POOL: Check if reserve can cover program creation
   */
  const checkReserveCapacity = useCallback((requiredCommits) => {
    const shortfall = Math.max(0, requiredCommits - reservePool.availableReserve);
    
    return {
      requiredCommits,
      availableReserve: reservePool.availableReserve,
      canCover: requiredCommits <= reservePool.availableReserve,
      shortfall,
      additionalPurchaseNeeded: shortfall > 0 ? shortfall : 0
    };
  }, [reservePool.availableReserve]);
  
  /**
   * Calculate commits for a program (deterministic)
   */
  const calculateCommits = useCallback((programData) => {
    return calculateProgramCommits(programData);
  }, []);
  
  /**
   * Simulate commits (uses same engine)
   */
  const simulateCommits = useCallback((simulationParams) => {
    return calculateProgramCommits(simulationParams);
  }, []);
  
  /**
   * Allocate commits to a program
   */
  const allocateCommits = useCallback((programId, commits) => {
    setGlobalCounters(prev => ({
      ...prev,
      commitsGenerated: prev.commitsGenerated + commits.totalCommits,
      monitoringCommits: prev.monitoringCommits + commits.grid.monitoring,
      bankCommits: prev.bankCommits + commits.grid.bankInfrastructure
    }));
    
    return {
      success: true,
      programId,
      allocated: commits.totalCommits
    };
  }, []);
  
  /**
   * Consume commits
   */
  const consumeCommits = useCallback((programId, amount, type = 'general') => {
    setGlobalCounters(prev => ({
      ...prev,
      commitsConsumed: prev.commitsConsumed + amount,
      commitsRemaining: prev.commitsGenerated - prev.commitsConsumed - amount
    }));
    
    return {
      success: true,
      consumed: amount,
      type
    };
  }, []);
  
  /**
   * Allocate grant commits
   */
  const allocateGrantCommits = useCallback((programId, amount) => {
    if (amount > grantPool.remainingGrants) {
      return { success: false, reason: 'Insufficient grant pool' };
    }
    
    setGrantPool(prev => ({
      ...prev,
      allocatedGrants: prev.allocatedGrants + amount,
      remainingGrants: prev.remainingGrants - amount
    }));
    
    setGlobalCounters(prev => ({
      ...prev,
      grantCommits: prev.grantCommits + amount
    }));
    
    return { success: true, allocated: amount };
  }, [grantPool.remainingGrants]);
  
  /**
   * Update SVUG metrics
   */
  const updateSVUG = useCallback((programValue, integrityScore) => {
    setSvugMetrics(prev => {
      const newActivePrograms = prev.activePrograms + 1;
      const newTotalValue = prev.totalValue + programValue;
      const newAvgScore = ((prev.averageIntegrityScore * prev.activePrograms) + integrityScore) / newActivePrograms;
      
      return {
        totalValue: newTotalValue,
        activePrograms: newActivePrograms,
        averageIntegrityScore: Math.round(newAvgScore * 10) / 10,
        totalCommitsConsumed: prev.totalCommitsConsumed
      };
    });
  }, []);
  
  /**
   * Get commit grid for display
   */
  const getCommitGrid = useCallback((programData) => {
    const result = calculateProgramCommits(programData);
    return result.grid;
  }, []);
  
  /**
   * Validate envelope compliance
   */
  const validateEnvelope = useCallback((programData) => {
    const result = calculateProgramCommits(programData);
    return {
      compliant: result.envelopeCompliant,
      calculated: result.calculatedCommits,
      limit: result.envelopeLimit,
      overage: result.envelopeCompliant ? 0 : result.calculatedCommits - result.envelopeLimit
    };
  }, []);
  
  // Computed values
  const economicSummary = useMemo(() => ({
    version: economicControls.currentVersion,
    commitPrice: ECONOMIC_VERSIONS[economicControls.currentVersion].commitPrice,
    globalCommits: globalCounters,
    svug: svugMetrics,
    grantPool
  }), [economicControls.currentVersion, globalCounters, svugMetrics, grantPool]);
  
  const value = {
    // Constants
    ECONOMIC_VERSIONS,
    CURRENT_ECONOMIC_VERSION,
    TOPOLOGY_WEIGHTS,
    THROUGHPUT_CONFIG,
    MONITORING_WEIGHTS,
    MONITORING_CAP_PERCENT,
    ENVELOPE_CAPS,
    BANK_SHARE_CAP_PERCENT,
    INITIALIZATION_COMMITS,
    
    // State
    globalCounters,
    svugMetrics,
    economicControls,
    grantPool,
    reservePool,  // PHASE 8: Reserve Pool state
    economicSummary,
    
    // Core Calculations (DETERMINISTIC)
    calculateCommits,
    simulateCommits,
    getCommitGrid,
    validateEnvelope,
    
    // Operations
    allocateCommits,
    consumeCommits,
    allocateGrantCommits,
    updateSVUG,
    
    // PHASE 8: Reserve Pool Operations
    moveToReserve,
    allocateFromReserve,
    purchaseToReserve,
    donateToGrantPool,
    getReserveSummary,
    checkReserveCapacity,
    
    // Static calculation functions (for use outside context)
    calculateTopologyCommits,
    calculateThroughputCommits,
    calculateMonitoringCommits,
    calculateEnvelopeLimit,
    calculateBankShare,
    calculateProgramCommits
  };
  
  return (
    <CommitEconomyContext.Provider value={value}>
      {children}
    </CommitEconomyContext.Provider>
  );
}

export function useCommitEconomy() {
  const context = useContext(CommitEconomyContext);
  if (!context) {
    throw new Error('useCommitEconomy must be used within CommitEconomyProvider');
  }
  return context;
}

export default CommitEconomyContext;
